/* Yooppee wishlist, phase 2: account sync for LOGGED-IN customers only
   (needs assets/wishlist.js; loaded with defer right after it).

   Ticket: snippets/wishlist-sync-config.liquid prints
   <script type="application/json" id="YP-Wishlist-Sync">{endpoint, customer_id,
   ts, sig}</script> only for a logged-in customer with a valid secret. No ticket
   means logged out (or the secret is missing): this file then does NOTHING and
   makes no network call at all. The service checks sig = HMAC-SHA256(secret,
   "<customer_id>:<ts>") and the ticket is short lived, so a 401 stops syncing
   for the rest of the page and the next reload gets a fresh signature.

   Local state
     localStorage 'yp_wishlist_owner_v1'    = customer id the local list belongs to
     localStorage 'yp_wishlist_synced_v1'   = {owner, handles[]} of the last successful sync
     localStorage 'yp_wishlist_queue_v1'    = event bodies that failed (max 50, oldest dropped)
     sessionStorage 'yp_wishlist_list_done_v1' = customer id whose /list already ran
     sessionStorage 'yp_wishlist_sid_v1'    = random id for this browser session

   Flow (once per page): flush the retry queue, then POST {endpoint}/merge when
   there is no snapshot for this owner (first login on this browser) or the local
   list changed since the snapshot (changed while logged out), else POST
   {endpoint}/list once per browser session. A successful answer is written back
   with YPWishlist.replace(list) - no analytics, no sync events - newest first,
   capped at 100, then the snapshot is stored. Later add/remove go out as
   {type:'add'|'remove',...} events and an add to cart as {type:'add_to_cart',...};
   every event carries this session's id.
   Another account on the same browser: the local list, queue and snapshot are
   reset, never merged into the new account. Errors never throw to the page. */
(function () {
  'use strict';

  var OWNER_KEY = 'yp_wishlist_owner_v1';
  var SNAP_KEY = 'yp_wishlist_synced_v1';
  var QUEUE_KEY = 'yp_wishlist_queue_v1';
  var DONE_KEY = 'yp_wishlist_list_done_v1';
  var SID_KEY = 'yp_wishlist_sid_v1';
  var QUEUE_MAX = 50;
  var RETRY_MAX = 60000;

  var W = null;
  var auth = null;
  var endpoint = '';
  var myId = '';
  var stopped = false; /* 401: the signature expired, next reload refreshes it */
  var tries = 0;
  var retryTimer = null;
  var booted = false;
  var finalized = false;

  /* ---- storage (a blocked localStorage must never break the page) ---- */
  function lsGet(k) {
    try {
      return window.localStorage.getItem(k);
    } catch (e) {
      return null;
    }
  }
  function lsSet(k, v) {
    try {
      window.localStorage.setItem(k, v);
    } catch (e) {
      /* ignore */
    }
  }
  function lsDel(k) {
    try {
      window.localStorage.removeItem(k);
    } catch (e) {
      /* ignore */
    }
  }
  function ssGet(k) {
    try {
      return window.sessionStorage.getItem(k);
    } catch (e) {
      return null;
    }
  }
  function ssSet(k, v) {
    try {
      window.sessionStorage.setItem(k, v);
    } catch (e) {
      /* ignore */
    }
  }
  function ssDel(k) {
    try {
      window.sessionStorage.removeItem(k);
    } catch (e) {
      /* ignore */
    }
  }

  function numOrNull(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  }
  function msOf(v) {
    var n = Number(v);
    if (!isFinite(n) || n <= 0) {
      return 0; /* unknown: sorts last, YPWishlist.replace() stamps it with now */
    }
    return n < 1e11 ? Math.round(n * 1000) : n; /* seconds -> ms */
  }
  function cleanTitle(value) {
    if (typeof value !== 'string') {
      return null;
    }
    var title = value.trim();
    return title ? title.slice(0, 255) : null;
  }
  function cleanVariantTitle(value) {
    var title = cleanTitle(value);
    return title && title.toLowerCase() !== 'default title' ? title : null;
  }
  function cleanPrice(value) {
    if (typeof value === 'number') {
      if (!isFinite(value) || value < 0) {
        return null;
      }
      value = String(value);
    }
    if (typeof value !== 'string') {
      return null;
    }
    var price = value.trim();
    return /^\d{1,8}(\.\d{1,2})?$/.test(price) ? price : null;
  }
  function cleanCurrency(value) {
    return typeof value === 'string' && /^[A-Z]{3}$/.test(value) ? value : null;
  }
  function entryMeta(entry) {
    var it = entry || {};
    var productTitle = cleanTitle(it.productTitle != null ? it.productTitle : it.product_title);
    var variantTitle = cleanVariantTitle(it.variantTitle != null ? it.variantTitle : it.variant_title);
    var price = cleanPrice(it.price);
    var currency = cleanCurrency(it.currency);
    var meta = {};
    if (productTitle) {
      meta.product_title = productTitle;
    }
    if (variantTitle) {
      meta.variant_title = variantTitle;
    }
    if (price) {
      meta.price = price;
    }
    if (currency && (price || productTitle || variantTitle)) {
      meta.currency = currency;
    }
    return meta;
  }

  function sid() {
    var s = ssGet(SID_KEY);
    if (s) {
      return s;
    }
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        s = window.crypto.randomUUID();
      }
    } catch (e) {
      s = null;
    }
    if (!s) {
      s = 's-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    }
    ssSet(SID_KEY, s);
    return s;
  }

  /* ---- snapshot: sorted handles of the list at the last successful sync ---- */
  function handlesNow() {
    try {
      return W.list().map(function (it) {
        return it && it.handle;
      }).filter(function (h) {
        return typeof h === 'string' && h !== '';
      }).sort();
    } catch (e) {
      return [];
    }
  }
  function writeSnap() {
    try {
      lsSet(SNAP_KEY, JSON.stringify({ owner: myId, handles: handlesNow() }));
    } catch (e) {
      /* ignore */
    }
  }
  function snapshotMatches() {
    var raw = lsGet(SNAP_KEY);
    if (!raw) {
      return false; /* no snapshot for this owner: first login on this browser */
    }
    var snap = null;
    try {
      snap = JSON.parse(raw);
    } catch (e) {
      snap = null;
    }
    if (!snap || snap.owner !== myId || !Array.isArray(snap.handles)) {
      return false;
    }
    var now = handlesNow();
    if (snap.handles.length !== now.length) {
      return false;
    }
    for (var i = 0; i < now.length; i++) {
      if (snap.handles[i] !== now[i]) {
        return false;
      }
    }
    return true; /* unchanged since the last successful sync */
  }

  /* ---- retry queue ---- */
  function readQueue() {
    var raw = lsGet(QUEUE_KEY);
    var q = [];
    if (raw) {
      try {
        q = JSON.parse(raw);
      } catch (e) {
        q = [];
      }
    }
    if (!Array.isArray(q)) {
      return [];
    }
    q = q.filter(function (body) {
      return typeof body === 'string' && body !== '';
    });
    /* cap 50, oldest dropped */
    return q.length > QUEUE_MAX ? q.slice(q.length - QUEUE_MAX) : q;
  }
  function writeQueue(q) {
    if (!q.length) {
      lsDel(QUEUE_KEY);
      return;
    }
    lsSet(QUEUE_KEY, JSON.stringify(q.length > QUEUE_MAX ? q.slice(q.length - QUEUE_MAX) : q));
  }
  function enqueue(body) {
    try {
      var q = readQueue();
      q.push(body);
      writeQueue(q);
    } catch (e) {
      /* ignore */
    }
  }

  /* ---- transport ---- */
  function transport(path, body, keepalive) {
    return window.fetch(endpoint + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body,
      keepalive: !!keepalive,
      mode: 'cors',
      credentials: 'omit'
    });
  }
  /* 'ok' | 'retry' (network error, 5xx, 429) | 'drop' (other 4xx) | 'auth' (401) */
  function fetchEvent(body) {
    return transport('/event', body, true).then(function (res) {
      if (res && res.ok) {
        return 'ok';
      }
      var status = res ? res.status : 0;
      if (status === 401) {
        stopped = true;
        return 'auth';
      }
      if (status === 429 || status >= 500) {
        return 'retry';
      }
      return 'drop';
    }, function () {
      return 'retry';
    });
  }
  function flushQueue() {
    var q = readQueue();
    if (!q.length || stopped) {
      return Promise.resolve();
    }
    var left = [];
    var chain = Promise.resolve();
    q.forEach(function (body) {
      chain = chain.then(function () {
        if (stopped) {
          left.push(body);
          return null;
        }
        return fetchEvent(body).then(function (kind) {
          if (kind === 'ok') {
            writeSnap(); /* the list this event described is on the server now */
            return null;
          }
          if (kind !== 'drop') {
            left.push(body); /* retry / 401: replay on the next load */
          }
          return null;
        });
      });
    });
    return chain.then(function () {
      writeQueue(left);
      if (!left.length) {
        tries = 0;
      }
    });
  }

  /* ---- events out (fire and forget, never blocking the UI) ---- */
  function eventPayload(type, item, source) {
    var it = item || {};
    var ev = {
      type: type,
      product_id: numOrNull(it.id),
      variant_id: numOrNull(it.variantId),
      handle: typeof it.handle === 'string' && it.handle !== '' ? it.handle : null,
      source: source || it.source || null,
      session_id: sid()
    };
    /* metadata comes from the stored entry, never from DOM scraping */
    var meta = entryMeta(it);
    if (meta.product_title) {
      ev.product_title = meta.product_title;
    }
    if (meta.variant_title) {
      ev.variant_title = meta.variant_title;
    }
    if (meta.price) {
      ev.price = meta.price; /* decimal major units, e.g. "12.34" */
    }
    if (meta.currency) {
      ev.currency = meta.currency;
    }
    return ev;
  }
  function send(type, item, source) {
    if (stopped || !endpoint || !auth) {
      return;
    }
    try {
      var body = JSON.stringify({ auth: auth, event: eventPayload(type, item, source) });
      fetchEvent(body).then(function (kind) {
        if (kind === 'ok') {
          writeSnap(); /* add/remove/in cart is on the server: no merge needed next load */
        } else if (kind === 'retry') {
          enqueue(body);
        } else if (kind === 'auth') {
          stopped = true;
        }
      });
    } catch (e) {
      /* errors must never reach the page; the queue keeps what it can */
    }
  }
  function onOnline() {
    if (stopped) {
      return;
    }
    window.clearTimeout(retryTimer);
    var delay = Math.min(RETRY_MAX, 1000 * Math.pow(2, Math.min(tries, 6)));
    retryTimer = window.setTimeout(function () {
      tries++;
      flushQueue().catch(function () {
        /* ignore */
      });
    }, delay);
  }
  function watchEvents() {
    try {
      W.onChange(function (detail) {
        if (!detail || stopped || (detail.type !== 'add' && detail.type !== 'remove')) {
          return; /* 'replace' (the synced answer) and cross-tab 'sync' stay local */
        }
        send(detail.type, detail.item, detail.source);
      });
    } catch (e) {
      /* ignore */
    }
    try {
      var publish = W.publish;
      if (typeof publish === 'function') {
        W.publish = function (name, entry, source) {
          var out;
          try {
            out = publish.call(W, name, entry, source);
          } catch (e) {
            /* the wishlist's own analytics must never break */
          }
          try {
            if (name === 'wishlist_add_to_cart') {
              send('add_to_cart', entry, source); /* the panel reports its own add to cart this way */
            }
          } catch (e) {
            /* ignore */
          }
          return out;
        };
      }
    } catch (e) {
      /* ignore */
    }
  }

  /* ---- the synced answer becomes the local list ---- */
  function applyServer(data) {
    var srv = Array.isArray(data) ? data : (data && (data.items || data.wishlist || data.list)) || [];
    if (!Array.isArray(srv)) {
      srv = [];
    }
    var list = srv.map(function (raw) {
      if (!raw || typeof raw.handle !== 'string' || raw.handle === '') {
        return null;
      }
      var entry = {
        handle: raw.handle,
        id: numOrNull(raw.product_id != null ? raw.product_id : raw.id),
        variantId: numOrNull(raw.variant_id != null ? raw.variant_id : raw.variantId),
        addedAt: msOf(raw.added_at != null ? raw.added_at : raw.addedAt),
        source: 'sync'
      };
      var meta = entryMeta(raw);
      if (meta.product_title) {
        entry.productTitle = meta.product_title;
      }
      if (meta.variant_title) {
        entry.variantTitle = meta.variant_title;
      }
      if (meta.price) {
        entry.price = meta.price;
      }
      if (meta.currency) {
        entry.currency = meta.currency;
      }
      return entry;
    }).filter(function (x) {
      return !!x;
    });
    list.sort(function (a, b) {
      return (b.addedAt || 0) - (a.addedAt || 0); /* newest first */
    });
    var cap = W.cap || 100;
    if (list.length > cap) {
      list.length = cap;
    }
    try {
      W.replace(list); /* hearts, count and the open panel refresh, nothing is published */
    } catch (e) {
      return false;
    }
    writeSnap();
    return true;
  }
  function mergeOnce() {
    var items = [];
    try {
      items = W.list().map(function (it) {
        var pid = numOrNull(it && it.id);
        if (!pid) {
          return null; /* entries without a numeric product id cannot sync */
        }
        var row = {
          handle: it.handle,
          product_id: pid,
          variant_id: numOrNull(it.variantId),
          added_at: it.addedAt,
          source: it.source || null
        };
        var meta = entryMeta(it);
        if (meta.product_title) {
          row.product_title = meta.product_title;
        }
        if (meta.variant_title) {
          row.variant_title = meta.variant_title;
        }
        if (meta.price) {
          row.price = meta.price;
        }
        if (meta.currency) {
          row.currency = meta.currency;
        }
        return row;
      }).filter(function (x) {
        return !!x;
      });
    } catch (e) {
      return Promise.resolve(false);
    }
    return transport('/merge', JSON.stringify({ auth: auth, items: items }), false).then(function (res) {
      if (!res || !res.ok) {
        if (res && res.status === 401) {
          stopped = true;
        }
        return false;
      }
      ssSet(DONE_KEY, myId); /* the merge answer is the whole list already */
      return res.json().then(function (data) {
        return applyServer(data) === true;
      }, function () {
        return false;
      });
    }).catch(function () {
      return false;
    });
  }
  function listOnce() {
    return transport('/list', JSON.stringify({ auth: auth, session_id: sid() }), false).then(function (res) {
      if (!res || !res.ok) {
        if (res && res.status === 401) {
          stopped = true;
        }
        return false;
      }
      ssSet(DONE_KEY, myId);
      return res.json().then(function (data) {
        return applyServer(data) === true;
      }, function () {
        return false;
      });
    }).catch(function () {
      return false;
    });
  }

  /* ---- boot ---- */
  function finishSync(action, ok) {
    if (finalized) {
      return;
    }
    finalized = true;
    if (action !== 'merge' && action !== 'list') {
      action = 'none';
    }
    var state = { done: true, ok: ok === true, action: action };
    window.YPWishlistSyncState = state;
    try {
      document.dispatchEvent(new CustomEvent('yp:wishlist:synced', {
        detail: { ok: state.ok, action: state.action }
      }));
    } catch (e) {
      /* the state above still lets late listeners continue */
    }
  }
  function start() {
    var el = document.getElementById('YP-Wishlist-Sync');
    if (!el) {
      return; /* logged out or no secret: no network calls at all */
    }
    var cfg = null;
    try {
      cfg = JSON.parse(el.textContent || el.innerHTML || '');
    } catch (e) {
      cfg = null;
    }
    if (!cfg || !cfg.endpoint || !cfg.customer_id || !cfg.sig || !cfg.ts || !window.fetch) {
      return;
    }
    W = window.YPWishlist;
    endpoint = String(cfg.endpoint).replace(/\/+$/, '');
    myId = String(cfg.customer_id);
    auth = { customer_id: myId, ts: String(cfg.ts), sig: String(cfg.sig) };
    if (!W || typeof W.replace !== 'function' || typeof W.list !== 'function' || typeof W.onChange !== 'function' || typeof W.publish !== 'function') {
      finishSync('none', false);
      return;
    }

    /* owner: another account may have used this browser - never merge its list */
    var owner = lsGet(OWNER_KEY);
    if (owner && owner !== myId) {
      try {
        W.replace([]);
      } catch (e) {
        /* ignore */
      }
      lsDel(QUEUE_KEY);
      lsDel(SNAP_KEY);
      ssDel(DONE_KEY);
    }
    lsSet(OWNER_KEY, myId);

    /* decided now, before the queue flush writes a fresh snapshot */
    var needMerge = !snapshotMatches();
    var needList = !needMerge && ssGet(DONE_KEY) !== myId;
    var action = needMerge ? 'merge' : (needList ? 'list' : 'none');

    watchEvents();
    window.addEventListener('online', onOnline);
    flushQueue().then(function () {
      if (stopped) {
        return false;
      }
      if (needMerge) {
        return mergeOnce();
      }
      if (needList) {
        return listOnce(); /* once per browser session */
      }
      return true;
    }).then(function (ok) {
      finishSync(action, ok === true);
    }).catch(function () {
      finishSync(action, false);
    });
  }

  function boot() {
    if (booted) {
      return;
    }
    if (!window.YPWishlist) {
      /* wishlist.js not ready yet (script order changed): give it one more chance */
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
      }
      return;
    }
    booted = true;
    try {
      start();
    } catch (e) {
      if (auth) {
        finishSync('none', false); /* a valid ticket that cannot start is still a failed initial flow */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
